/**
 * #1245 — interaction-tagging wrapper around native `fetch`.
 *
 * The #1220 gate bumps "Last Active" / WAU only when a request carries
 * `X-User-Interaction: true`, a marker the axios `api` client adds via
 * `tagInteractiveRequest`. Genuinely-interactive paths that use native
 * `fetch()` (admin pages, OAuth/connection management) never went through axios
 * and so stopped bumping the signal. This thin wrapper mirrors the exact axios
 * logic — it reuses the same `interactionSignal` gesture-recency check (no
 * duplicated logic) and tags ONLY same-origin `/api` requests, so the marker
 * never leaks to third-party OAuth/redirect URLs.
 */

import { wasRecentlyInteractive } from './interactionSignal';
import { INTERACTION_HEADER } from './api';

/**
 * True iff `input` targets a same-origin request under the `/api` prefix —
 * i.e. a call the BFF proxies and the backend reads the interaction marker on.
 * Cross-origin URLs (third-party OAuth consent screens, CDNs) and same-origin
 * non-`/api` paths (e.g. `/documents/*`) are excluded so the header never leaks.
 */
function isSameOriginApiRequest(input: RequestInfo | URL): boolean {
  const url = input instanceof Request ? input.url : input.toString();
  // Root-relative `/api/...` is same-origin by construction.
  if (url.startsWith('/api')) {
    return true;
  }
  try {
    const resolved = new URL(url, window.location.origin);
    return resolved.origin === window.location.origin && resolved.pathname.startsWith('/api');
  } catch {
    return false;
  }
}

/**
 * Drop-in `fetch` replacement that tags genuine user-initiated `/api` calls.
 *
 * A request counts as interactive when it follows a real gesture within
 * `INTERACTION_WINDOW_MS` (see `interactionSignal`) — identical to the axios
 * interceptor. Caller-supplied headers are preserved; the marker is merged in.
 */
export function taggedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  if (isSameOriginApiRequest(input) && wasRecentlyInteractive()) {
    const headers = new Headers(init?.headers);
    headers.set(INTERACTION_HEADER, 'true');
    return fetch(input, { ...init, headers });
  }
  return fetch(input, init);
}
